import React, { useState, useRef, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import { RequiredAsterisk } from '../../../components/common/RequiredAsterisk';

interface MultiSelectProps {
  label?: string;
  options: string[];
  value?: string[] | null;
  onChange: (value: string[]) => void;
  required?: boolean;
  placeholder?: string;
  maxSelections?: number;
  disabled?: boolean;
  error?: string;
  touched?: boolean;
  name?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ 
  label, 
  options, 
  value = [], 
  onChange, 
  required, 
  placeholder = "Select options", 
  maxSelections = Infinity,
  disabled = false,
  error,
  touched,
  name
}) => {

  
  const safeValue = value ?? [];
  
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasError = error && touched;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard typing to filter options
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!isOpen || disabled) return;

      // Handle typing for search
      if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        setSearchTerm(prev => prev + e.key);
      }
      // Handle backspace
      if (e.key === 'Backspace') {
        setSearchTerm(prev => prev.slice(0, -1));
      }
      // Handle escape
      if (e.key === 'Escape') {
        setSearchTerm('');
        setIsOpen(false);
      }
      // Handle Enter to select first filtered option
      if (e.key === 'Enter' && filteredOptions.length > 0) {
        e.preventDefault();
        const firstOption = filteredOptions[0];
        if (!safeValue.includes(firstOption)) {
          handleOptionClick(firstOption);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isOpen, disabled, searchTerm]);

  // Clear search when closing dropdown
  useEffect(() => {
    if (!isOpen) setSearchTerm('');
  }, [isOpen]);

  // Handles option selection/deselection and enforces selection limit
  const handleOptionClick = useCallback((optionValue: string) => {
    
    if (disabled) return;
    
    const isSelected = safeValue.includes(optionValue);

    if (!isSelected && safeValue.length >= maxSelections) {
      Swal.fire({
        icon: "warning",
        title: "Selection Limit Reached",
        text: `You can only select up to ${maxSelections} item${maxSelections > 1 ? 's' : ''}.`,
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    const newValue = isSelected
      ? safeValue.filter(v => v !== optionValue)
      : [...safeValue, optionValue];

    onChange(newValue);
    
   
    
    // Clear search term after selection to show full list
    setSearchTerm('');
  }, [disabled, safeValue, maxSelections, onChange]);
  

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Determine display text for the select button
  const displayValue = safeValue.length ? 
    safeValue.length > 2 ? 
      `${safeValue.filter(opt=>opt != "Select All").slice(0, 2).join(', ')} +${safeValue.length - 2} more` : 
      safeValue.join(', ') 
    : placeholder;

  // Handle clear all selections
  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      onChange([]);
    }
  };
  const handleSelectAll = () => {
    const actualOptions = filteredOptions.filter(opt => opt !== "SelectAll");
  
    const allSelected = actualOptions.every(opt => safeValue.includes(opt));
  
    if (allSelected) {
      // Unselect all actual options (not including "SelectAll")
      const remaining = safeValue.filter(val => !actualOptions.includes(val));
      onChange(remaining);
    } else {
      // Select all actual options (up to maxSelections if applicable)
      const remainingSlots = maxSelections ? maxSelections - safeValue.length : Infinity;
      const newSelections = actualOptions.filter(opt => !safeValue.includes(opt)).slice(0, remainingSlots);
      onChange([...safeValue, ...newSelections]);
    }
  };
  

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {label && (
        <label className="block mb-2 text-sm font-medium text-gray-900">
          {label}
          {required && <RequiredAsterisk />}
        </label>
      )}

      <div className={`relative w-full ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <div 
          className={`flex justify-between items-center px-3 py-2 text-sm border rounded-md bg-white cursor-pointer transition-colors ${
            hasError 
              ? 'border-red-500 border-2' 
              : 'border-gray-300 hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200'
          } ${disabled ? 'cursor-not-allowed' : ''}`}
          onClick={() => !disabled && setIsOpen(prev => !prev)}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={label || 'Multi-select dropdown'}
        >
          
          <div 
            className="text-sm text-gray-700 placeholder-gray-500 truncate" 
            title={safeValue.filter(opt => opt != "Select All").join(', ')}
          >
            <span className={safeValue.length ? 'text-gray-700' : 'text-gray-500'}>
              {displayValue}
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            {safeValue.length > 0 && !disabled && (
              <button
                type="button"
                className="flex items-center justify-center w-4 h-4 text-sm font-bold leading-none text-gray-400 transition-colors rounded-full hover:text-gray-600 hover:bg-gray-100"
                onClick={handleClearAll}
                title="Clear all selections"
                aria-label="Clear all selections"
              >
                ×
              </button>
            )}
            <span className={`text-gray-400 text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </div>
        </div>

        {isOpen && !disabled && (
          <div className="absolute left-0 right-0 z-50 mt-1 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg top-full max-h-48" role="listbox">
            {/* Show current search term if user is typing */}
            {searchTerm && (
              <div className="p-2 text-sm text-gray-600 border-b border-gray-200 bg-gray-50">
                Searching for: "<strong>{searchTerm}</strong>"
                {filteredOptions.length > 0 && (
                  <span className="ml-1 text-gray-500">
                    ({filteredOptions.length} result{filteredOptions.length !== 1 ? 's' : ''})
                  </span>
                )}
              </div>
            )}

            {/* Selection counter */}
            {maxSelections !== Infinity && (
              <div className="p-2 text-xs text-gray-500 border-b border-gray-200 bg-gray-50">
                {safeValue.length} / {maxSelections} selected
              </div>
            )}

            <div className="py-1">
              {filteredOptions.length > 0 ? (
                 filteredOptions.map((option) => (
                  option==="Select All" ? (

                 <div
                 className={`flex items-center px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-gray-50 ${
                  filteredOptions
                    .filter(opt => opt !== "Select All")
                    .every(opt => safeValue.includes(opt))
                    ? 'bg-blue-50 text-blue-900'
                    : 'text-gray-900'
                }`} 
                 onClick={handleSelectAll}
               >
                 <input
                   type="checkbox"
                   readOnly
                   
                   checked={filteredOptions.filter(opt => opt !== "Select All").every(opt => safeValue.includes(opt))}
                   className="w-4 h-4 mr-2 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                 />
                <span className="flex-1 text-sm text-gray-700">{option}</span>
               </div>):(
                  <div
                    key={option}
                    className={`flex items-center px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-gray-50 ${
                      safeValue.includes(option) ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                    }`}
                    onClick={() => handleOptionClick(option)}
                    role="option"
                    aria-selected={safeValue.includes(option)}
                  >
                    <input
                      type="checkbox"
                      checked={safeValue.includes(option)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleOptionClick(option)}
                      className="w-4 h-4 mr-2 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      tabIndex={-1}
                      aria-hidden="true"
                      required={required} 
                    />
                    <span className="flex-1 text-sm text-gray-700">{option}</span>
                  </div>)
                ))
                
              ) : (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {searchTerm ? (
                    <>
                      No results for "<strong>{searchTerm}</strong>"
                      <div className="mt-1 text-xs text-gray-400">Try a different search term</div>
                    </>
                  ) : (
                    'No options available'
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error message */}
      {hasError && (
        <div className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </div>
      )}

      {/* Hidden input for form submission */}
      {name && (
        <input
          type="hidden"
          name={name}
          value={safeValue.join(',')}
        />
      )}
    </div>
  );
};

export default MultiSelect;
